import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

export interface LibraDocumentContextValue {
  selectedDocumentId: string | null
  setSelectedDocumentId: (documentId: string | null) => void
  unitId: string | undefined
}

const LibraDocumentContext = createContext<
  LibraDocumentContextValue | undefined
>(undefined)

export interface LibraDocumentProviderProps {
  children: ReactNode
  unitId: string | undefined
}

export const LibraDocumentProvider: FC<LibraDocumentProviderProps> = ({
  children,
  unitId,
}) => {
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null
  )

  const handleSetSelectedDocumentId = useCallback(
    (documentId: string | null) => {
      setSelectedDocumentId(documentId)
    },
    []
  )

  const value = useMemo<LibraDocumentContextValue>(
    () => ({
      selectedDocumentId,
      setSelectedDocumentId: handleSetSelectedDocumentId,
      unitId,
    }),
    [selectedDocumentId, handleSetSelectedDocumentId, unitId]
  )

  return (
    <LibraDocumentContext.Provider value={value}>
      {children}
    </LibraDocumentContext.Provider>
  )
}

export const useLibraDocument = (): LibraDocumentContextValue => {
  const context = useContext(LibraDocumentContext)
  if (!context) {
    throw new Error(
      'useLibraDocument must be used within a LibraDocumentProvider'
    )
  }
  return context
}
